// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { Button } from "@/components/ui/button"
import { useI18n } from "@/i18n"
import type { FinishPhase } from "./types"

interface FinishingScreenProps {
  /** Error code · correlationId — makes a live "couldn't start" report diagnosable (2026-08-29 incident). */
  errorDetail?: string | null
  /** "ready" is the brief instant between a resolved poll and the
   * navigate() call — rendered with the same honest spinner as
   * submitting/waitingForPlan so there's never a one-frame flash back to
   * the step form while the router transitions to Access. */
  phase: Exclude<FinishPhase, "idle">
  onRetry: () => void
  onContinueAnyway: () => void
}

/**
 * Full-screen state between step 12's submit and landing on Access (item
 * 35) — replaces the old "button label changes to Saving…, then the whole
 * page is silently swapped for an unrelated billing page" flow. Honest
 * throughout: no fake percentage, no fabricated "done" state — a real spinner
 * while the plan job is polled, a truthful timeout with a way to continue,
 * and a retryable error if the job never started at all.
 */
export function FinishingScreen({
  errorDetail = null, phase, onRetry, onContinueAnyway }: FinishingScreenProps) {
  const { t } = useI18n()

  if (phase === "timedOut") {
    return (
      <section
        role="status"
        aria-live="polite"
        aria-labelledby="finishing-timed-out-title"
        className="mx-auto flex w-full max-w-xl flex-col items-center gap-4 rounded-bb-xl border border-line bg-paper p-8 text-center"
      >
        <h1 id="finishing-timed-out-title" className="text-xl font-bold text-ink">
          {t("onboarding.finishing.timedOutTitle")}
        </h1>
        <p className="text-sm text-ink-2">{t("onboarding.finishing.timedOutBody")}</p>
        <Button type="button" className="min-h-11" onClick={onContinueAnyway}>
          {t("onboarding.finishing.continueAnyway")}
        </Button>
      </section>
    )
  }

  if (phase === "failed") {
    // 2026-08-25 review I2: a job that ENDED failed must never be presented
    // as success. Honest copy + continue (Today shows the real plan state
    // and its regenerate affordance).
    return (
      <section
        role="alert"
        aria-labelledby="finishing-failed-title"
        className="mx-auto flex w-full max-w-xl flex-col items-center gap-4 rounded-bb-xl border border-gold/40 bg-gold/5 p-8 text-center"
      >
        <h1 id="finishing-failed-title" className="text-xl font-bold text-ink">
          {t("onboarding.finishing.failedTitle")}
        </h1>
        <p className="text-sm text-ink-2">{t("onboarding.finishing.failedBody")}</p>
        <Button type="button" className="min-h-11" onClick={onContinueAnyway}>
          {t("onboarding.finishing.continueAnyway")}
        </Button>
      </section>
    )
  }

  if (phase === "error") {
    return (
      <section
        role="alert"
        aria-labelledby="finishing-error-title"
        className="mx-auto flex w-full max-w-xl flex-col items-center gap-4 rounded-bb-xl border border-destructive/30 bg-destructive/5 p-8 text-center"
      >
        <h1 id="finishing-error-title" className="text-xl font-bold text-ink">
          {t("onboarding.finishing.errorTitle")}
        </h1>
        <p className="text-sm text-ink-2">{t("onboarding.finishing.errorBody")}</p>
        {errorDetail && (
          <p className="text-xs text-muted-foreground">
            {t("onboarding.finishing.errorDetail", { detail: errorDetail })}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button type="button" className="min-h-11" onClick={onRetry}>
            {t("state.retry")}
          </Button>
          <Button type="button" variant="ghost" className="min-h-11" onClick={onContinueAnyway}>
            {t("onboarding.finishing.continueAnyway")}
          </Button>
        </div>
      </section>
    )
  }

  // "submitting" | "waitingForPlan" | "ready"
  return (
    <section
      role="status"
      aria-live="polite"
      aria-labelledby="finishing-title"
      className="mx-auto flex w-full max-w-xl flex-col items-center gap-4 rounded-bb-xl border border-line bg-paper p-10 text-center"
    >
      <span
        aria-hidden="true"
        className="size-10 animate-spin rounded-full border-4 border-line border-t-green"
      />
      <h1 id="finishing-title" className="text-xl font-bold text-ink">
        {t("onboarding.finishing.title")}
      </h1>
      <p className="text-sm text-ink-2">{t("onboarding.finishing.body")}</p>
    </section>
  )
}
